// Server-authoritative card-state read/write path (card-state domain D4).
//
// Per-card non-zone, non-resource state (tap, control-flip, stats, statuses,
// counters, subsurface, equipment, misc flags) becomes sequenced commands
// instead of fire-and-forget P2P (card:tap / card:ownership / card:stat /
// card:status / card:counters / card:subsurface / card:equipment / card:skip*).
// These are PUBLIC board state. An in-play card's tap/stats/statuses are visible
// to both seats. So the server projection never strips a play-zone card and the
// authoritative diff is identical for both. The actor mutates LOCALLY first (the
// legacy applyTap / applyControllerFlip / stat-buff ops already moved the mesh on
// its own screen), THEN emits the command; the diff confirms it and drives the
// same change on the other client via the mirror fold below.
//
// Field-name bridge: the authoritative document uses clean names
// (movementBonus / counters / castLockOwnerId / …); the client card instance uses
// the legacy underscored names (_movementBonus / _counters / _castLockOwnerId).
// Convert at THIS boundary, nowhere else.

import { playSound } from '../../../utils/game/sounds';
import {
  SET_TAPPED,
  SET_CONTROLLER,
  SET_CARD_STATS,
  SET_STATUSES,
  SET_COUNTERS,
  SET_SUBSURFACE,
  SET_EQUIPMENT,
  SET_CARD_FLAGS,
  SET_ANIMATE,
  SET_IDENTITY,
} from '../../../utils/game/net/matchCore';
import { applyAnimateSync, revertAnimateAuraTransform } from '../../../utils/game/engine/automation';
import { updateCardFrontTexture } from '../../../utils/game/cardMesh';
import { snapCardOrientation } from '../../../utils/game/animations';
import { cardLogLabel } from './logbookChat';
import { msg } from '../../../utils/game/logMessage';

// ── Command builders ───────────────────────────────────────────────────────

export function tapCommand(cardId, tapped) {
  return { type: SET_TAPPED, payload: { cardId, tapped } };
}

export function controllerCommand(cardId, rotated) {
  return { type: SET_CONTROLLER, payload: { cardId, rotated } };
}

// Build a SET_CARD_STATS command for a single legacy stat change. `stat` is the
// legacy 'atk' | 'hp' | 'mov'; for a manual hp edit the buffed-life delta rides
// along so the opponent's end-of-turn heal restores to the same value (legacy
// _manualLifeDelta semantics).
export function statsCommand(host, cardId, stat, value, opts = {}) {
  const payload = { cardId };
  if (stat === 'atk') payload.currentAttack = value;
  else if (stat === 'hp') {
    payload.currentLife = value;
    if (opts.manual) {
      const card = host.meshes?.get(cardId)?.userData?.cardInstance;
      payload.manualLifeDelta = Number(value) - (card?.baseLife ?? 0);
    }
  } else if (stat === 'mov') payload.movementBonus = value;
  else if (stat === 'aura') payload.auraStatDelta = value;
  return { type: SET_CARD_STATS, payload };
}

export function statusesCommand(cardId, statuses) {
  return { type: SET_STATUSES, payload: { cardId, statuses: Array.isArray(statuses) ? statuses : [] } };
}

export function countersCommand(cardId, counters) {
  return { type: SET_COUNTERS, payload: { cardId, counters: counters || 0 } };
}

export function subsurfaceCommand(cardId, subsurface) {
  return { type: SET_SUBSURFACE, payload: { cardId, subsurface: subsurface || null } };
}

export function equipmentCommand(cardId, equippedCards) {
  return { type: SET_EQUIPMENT, payload: { cardId, equippedCards: Array.isArray(equippedCards) ? equippedCards : [] } };
}

export function flagsCommand(cardId, flags) {
  return { type: SET_CARD_FLAGS, payload: { cardId, ...flags } };
}

// Enchantress aura→minion. `animatePayload` is animateSyncPayload(card) (type +
// base/current stats + revert snapshot), or null to revert.
export function animateCommand(cardId, animatePayload) {
  return { type: SET_ANIMATE, payload: { cardId, animate: animatePayload || null } };
}

// Imposter mask / Dragonlord borrow. Build the masked identity from the card's
// CURRENT (already-masked) fields + the pre-mask snapshot. Imposter's snapshot
// lives on `_originalIdentity`; Dragonlord passes its `_dragonIdentitySnapshot`
// via `original`. A null identity command (identityRestoreCommand) reverts from
// the server-stored snapshot.
export function identityCommandFromCard(card, { dragonlord = false, original } = {}) {
  return {
    type: SET_IDENTITY,
    payload: {
      cardId: card.id,
      identity: {
        cardId: card.cardId,
        name: card.name,
        imageUrl: card.imageUrl,
        imagePath: card.imagePath,
        foiling: card.foiling,
        subTypes: card.subTypes,
        elements: card.elements,
        printedKeywords: card._printedKeywords,
        movementBonus: card._movementBonus,
        rangedValue: card._rangedValue,
        dragonlordBorrow: dragonlord,
        original: original ?? card._originalIdentity ?? null,
      },
    },
  };
}

export function identityRestoreCommand(cardId) {
  return { type: SET_IDENTITY, payload: { cardId, identity: null } };
}

// ── Receiver: fold the authoritative card state onto the rendered meshes ─────

// Compare a mirror value to the live card value (arrays compared by content).
function changed(a, b) {
  if (Array.isArray(a) || Array.isArray(b)) return JSON.stringify(a) !== JSON.stringify(b);
  return a !== b;
}

// Idempotent over ALL cards (no seat skip): the actor's own cards already match
// the mirror (the optimistic local apply ran before the emit), so they are
// no-ops; the other client applies the change. Applying to all cards. Not just
// the owner's seat. Is required because control-change / opponent effects mutate
// a card the actor does not own. Visual drivers run with broadcast:false (no
// re-emit loop) and source:'remote' (skip the legality gates. The server
// already vetted). Renderer-coupled; validated by the manual 2-client pass.
export function applyCardStateFromMirror(host, mirrorState) {
  const cards = mirrorState?.cards;
  if (!cards) return;

  let aurasDirty = false;
  let affinityDirty = false;

  for (const [instanceId, mc] of Object.entries(cards)) {
    if (!mc) continue;
    const mesh = host.meshes?.get(instanceId);
    if (!mesh) continue;
    const card = mesh.userData?.cardInstance;
    if (!card) continue;

    // Tap (visual flip via the unified tap op). A staged combat defender's
    // preview tap (paid at drag-drop, server-invisible until the defend
    // commits) owns the visual. Folding the mirror's tapped:false over it
    // un-tapped the defender mid-selection whenever any diff arrived.
    const stagedDefender = host._combatSelect?.chosen?.has?.(instanceId);
    // A mover's optimistic move-tap runs AHEAD of the mirror while its move
    // intent is in flight. Folding the stale pre-move tapped:false over it
    // made every unrelated diff in the round-trip window untap-then-retap
    // the mover (with a double tap sound). The confirming diff (cell at the
    // requested target) reopens the fold; a rejected move heals through the
    // pending-move sweep's re-fold.
    const moveInFlight = !!host._isMoveInFlight?.(instanceId, mirrorState);
    if (typeof mc.tapped === 'boolean' && card.tapped !== mc.tapped && !stagedDefender && !moveInFlight) {
      host._applyTap(card, mesh, mc.tapped, { source: 'remote', broadcast: false, log: false, sound: true });
    } else if (typeof mc.tapped === 'boolean' && !host.dragging?.draggingIds?.has(instanceId)) {
      // Self-heal a pure-visual orientation desync: card.tapped already matches
      // the authoritative mirror, but the mesh orientation drifted away from it
      // (a local move-and-attack / region-transition animation reset intentEuler
      // without a state change. The attacker's own minion "untapping" mid-attack
      // while the server + the other client stay tapped). The render must be a
      // pure function of card state, so re-derive the orientation from it here;
      // without this the stuck visual only healed on a full reload. No-ops when
      // the orientation already matches (the steady-state case). Skip a card the
      // local player is dragging. The cursor owns its pose.
      snapCardOrientation(mesh, card);
    }
    // Control-flip (recompute auras + reconcile sickness via the unified op).
    if (typeof mc.rotated === 'boolean' && card.rotated !== mc.rotated) {
      host._applyControllerFlip(card, mesh, mc.rotated, { broadcast: false, log: false });
    }

    // Printed base stats (server-authoritative. The server hydrates these from
    // the card catalog, lazily on first combat touch). Fold them BEFORE the
    // current values so the damage HUD's delta (currentLife - baseLife) is
    // computed against the right base. Without this an opponent minion whose
    // mesh never locally initialized baseLife shows currentLife - undefined = 0
    // damage (it looks unhurt even after taking a hit).
    if (mc.baseAttack !== undefined && card.baseAttack !== mc.baseAttack) {
      card.baseAttack = mc.baseAttack;
      host._refreshLifeHUD?.(card);
    }
    if (mc.baseLife !== undefined && card.baseLife !== mc.baseLife) {
      card.baseLife = mc.baseLife;
      host._refreshLifeHUD?.(card);
    }

    // Static-aura derivation (P3): the server now DERIVES these continuous-effect
    // fields via the shared deriveStaticAuras hook and projects them down, so the
    // client ADOPTS them (was client-derived + locally trusted). A local
    // recomputeStaticAuras still runs for optimistic render between diffs and is
    // overwritten by the next authoritative fold. The lifeHUD refresh follows the
    // delta so the ATK/HP pill reflects the buff immediately.
    if (mc._auraStatDelta !== undefined && card._auraStatDelta !== mc._auraStatDelta) {
      card._auraStatDelta = mc._auraStatDelta;
      host._refreshLifeHUD?.(card);
    }
    if (mc._auraStatuses !== undefined && changed(mc._auraStatuses, card._auraStatuses)) {
      card._auraStatuses = Array.isArray(mc._auraStatuses) ? [...mc._auraStatuses] : [];
      host.rebuildStatusBadges?.(instanceId);
      aurasDirty = true;
    }
    if (mc._auraSuppressedKeywords !== undefined && changed(mc._auraSuppressedKeywords, card._auraSuppressedKeywords)) {
      card._auraSuppressedKeywords = Array.isArray(mc._auraSuppressedKeywords) ? [...mc._auraSuppressedKeywords] : [];
      aurasDirty = true;
    }
    // Player/server deny list. The server pushes onto it when a printed-keyword
    // token is consumed (Ward absorbed a hit: consumeWardStatus suppresses the
    // printed keyword so the badge doesn't re-grant it). Without this fold the
    // badge union re-added ward from _printedKeywords and the site rendered as
    // warded forever after the server had consumed it.
    if (mc._suppressedKeywords !== undefined && changed(mc._suppressedKeywords, card._suppressedKeywords)) {
      card._suppressedKeywords = Array.isArray(mc._suppressedKeywords) ? [...mc._suppressedKeywords] : [];
      // Mark the folded value as already-emitted. RecomputeStaticAuras' kw-emit
      // cache (_lastKwEmit) re-sends any local array that differs from its last
      // emit; without this stamp a fold arriving in a radial-toggle's round-trip
      // window would be echoed back to the server as if the player had set it,
      // and the two echo streams then alternate indefinitely (fold → emit →
      // diff → fold …). A folded value is server truth, never re-emit it.
      if (host._lastKwEmit) {
        const prev = host._lastKwEmit.get(card.id) || { printed: card._printedKeywords || [] };
        host._lastKwEmit.set(card.id, { ...prev, suppressed: [...card._suppressedKeywords] });
      }
      host.rebuildStatusBadges?.(instanceId);
      aurasDirty = true;
    }
    // The remaining server-derived static-aura fields (W2): threshold pair
    // feeds the casting-pool/affinity calc (affinityDirty, like
    // _providesThisTurn); subtype map / name aliases / granted triggers feed
    // filters and the action menu (aurasDirty).
    if (mc._thresholdSuppression !== undefined
      && JSON.stringify(mc._thresholdSuppression ?? null) !== JSON.stringify(card._thresholdSuppression ?? null)) {
      card._thresholdSuppression = mc._thresholdSuppression ? { ...mc._thresholdSuppression } : null;
      affinityDirty = true;
    }
    if (mc._thresholdMultiplier !== undefined && card._thresholdMultiplier !== mc._thresholdMultiplier) {
      card._thresholdMultiplier = mc._thresholdMultiplier;
      affinityDirty = true;
    }
    if (mc._auraSubtypeMap !== undefined
      && JSON.stringify(mc._auraSubtypeMap ?? null) !== JSON.stringify(card._auraSubtypeMap ?? null)) {
      card._auraSubtypeMap = mc._auraSubtypeMap ? { ...mc._auraSubtypeMap } : null;
      aurasDirty = true;
    }
    if (mc._auraNameAliases !== undefined && changed(mc._auraNameAliases, card._auraNameAliases)) {
      card._auraNameAliases = Array.isArray(mc._auraNameAliases) ? [...mc._auraNameAliases] : null;
      aurasDirty = true;
    }
    if (mc._grantedTriggers !== undefined && changed(mc._grantedTriggers, card._grantedTriggers)) {
      card._grantedTriggers = Array.isArray(mc._grantedTriggers) ? mc._grantedTriggers.map((e) => ({ ...e })) : [];
      aurasDirty = true;
    }

    // Stats (absolute primaries; effective/aura deltas stay client-derived).
    // Temp attack deltas fold FIRST and with presence semantics (absent on
    // the mirror → cleared): effectiveLife counts them as damage capacity,
    // and the currentLife fold below runs the death sweep. A sweep that
    // can't see the server's buff kills a minion the server kept alive
    // (Gift of the Wolf #59 / Hemogolem #26).
    const foldDelta = (key) => {
      const v = mc[key] ?? null;
      if ((card[key] ?? null) !== v) {
        if (v === null) delete card[key];
        else card[key] = v;
      }
    };
    foldDelta('_thisTurnAttackDelta');
    foldDelta('_untilNextTurnAttackDelta');
    if (mc.currentAttack !== undefined && card.currentAttack !== mc.currentAttack) {
      card.currentAttack = mc.currentAttack;
      host._refreshLifeHUD?.(card);
    }
    if (mc.currentLife !== undefined && card.currentLife !== mc.currentLife) {
      card.currentLife = mc.currentLife;
      host._refreshLifeHUD?.(card);
      host._sweepDeadMinions?.();
    }
    if (mc.movementBonus !== undefined && card._movementBonus !== mc.movementBonus) {
      card._movementBonus = mc.movementBonus;
      host._refreshMovementHUD?.(instanceId);
    }
    if (mc.manualLifeDelta !== undefined) card._manualLifeDelta = mc.manualLifeDelta;

    // Statuses (absolute keyword-token array).
    if (mc.statuses !== undefined && changed(mc.statuses, card.statuses)) {
      card.statuses = Array.isArray(mc.statuses) ? [...mc.statuses] : [];
      host.rebuildStatusBadges?.(instanceId);
      host._refreshMovementHUD?.(instanceId);
      aurasDirty = true;
    }

    // Counters.
    if (mc.counters !== undefined && card._counters !== mc.counters) {
      card._counters = mc.counters;
      host._refreshCounterAnchorFor?.(instanceId);
    }

    // Subsurface (burrow/submerge region flag; stack tier re-lays the mesh Y).
    if (mc.subsurface !== undefined && card.subsurface !== mc.subsurface) {
      card.subsurface = mc.subsurface;
      aurasDirty = true;
    }

    // Summoning sickness is SERVER truth: castService stamps it on cast and
    // the turn boundary clears it for every card. It used to be client-local
    // only, and the defending client never runs the opponent's START step
    // that clears it. CanDefend then read a stale flag and silently
    // auto-declined defend prompts (GH-67). Presence semantics: absent on
    // the mirror → cleared.
    const mirrorSick = mc._hasSummoningSickness ?? null;
    if ((card._hasSummoningSickness ?? null) !== mirrorSick) {
      if (mirrorSick === null) delete card._hasSummoningSickness;
      else card._hasSummoningSickness = mirrorSick;
      // The sickness badge / sleep glyphs derive from this flag.
      host.rebuildStatusBadges?.(instanceId);
      if (host.hoveredMesh?.userData?.cardInstance?.id === instanceId) {
        host.syncHoverStatusOverlay?.();
      }
    }

    // Equipment (full array replace). The mirror array + its element objects are
    // immer-frozen (applyPatches auto-freezes). Aliasing them by reference makes
    // a later equip (push a 2nd artifact) or detach (splice / mutate the freed
    // card's x/z/subsurface) throw "object is not extensible" and silently abort.
    // Deep-clone to hand the board a fully-mutable copy the equip/detach ops own.
    if (mc.equippedCards !== undefined && changed(mc.equippedCards, card.equippedCards)) {
      card.equippedCards = Array.isArray(mc.equippedCards) ? structuredClone(mc.equippedCards) : [];
      host.rebuildStatusBadges?.(instanceId);
      host.syncEquipmentOverlays?.();
      // Legacy-handler parity: equipping a counter card (Immortal Throne as a
      // Relic) surfaces the bearer's level token, and a hovered card's status
      // overlay must refresh NOW, not on the next re-hover.
      host._refreshCounterHUD?.(instanceId);
      if (host.hoveredMesh?.userData?.cardInstance?.id === instanceId) {
        host.syncHoverStatusOverlay?.();
      }
      aurasDirty = true;
    }

    // Enchantress animate (aura→minion) / revert. Flag transition driven.
    if (mc.enchantressAnimated && !card._enchantressAnimated) {
      applyAnimateSync(card, {
        type: mc.type, subTypes: mc.subTypes,
        baseAttack: mc.baseAttack, baseLife: mc.baseLife,
        currentAttack: mc.currentAttack, currentLife: mc.currentLife,
        original: mc.enchantressOriginal,
      });
      host._attachLifeHUDIfMissing?.(card);
      host._refreshLifeHUD?.(card);
      aurasDirty = true;
    } else if (mc.enchantressAnimated === false && card._enchantressAnimated) {
      if (revertAnimateAuraTransform(card)) {
        host._detachLifeHUD?.(card);
        aurasDirty = true;
      }
    }

    // Identity transform (Imposter mask / Dragonlord borrow / unmask). Triggers
    // only when an identity field legitimately changed. A non-masked card's
    // authoritative cardId/name never moves, so this is a no-op for it.
    const identityChanged = mc.name !== undefined
      && (mc.cardId !== card.cardId || mc.name !== card.name || !!mc.dragonlordBorrow !== !!card._dragonlordBorrow);
    if (identityChanged) {
      card.cardId = mc.cardId;
      card.name = mc.name;
      card.imageUrl = mc.imageUrl;
      if (mc.imagePath !== undefined) card.imagePath = mc.imagePath;
      if (mc.foiling !== undefined) card.foiling = mc.foiling;
      if (mc.subTypes !== undefined) card.subTypes = mc.subTypes;
      if (mc.elements !== undefined) card.elements = mc.elements;
      if (mc.printedKeywords !== undefined) card._printedKeywords = mc.printedKeywords;
      if (mc.rangedValue !== undefined) card._rangedValue = mc.rangedValue;
      const borrowStarted = !!mc.dragonlordBorrow && !card._dragonlordBorrow;
      card._dragonlordBorrow = !!mc.dragonlordBorrow;
      // The pre-mask snapshot rides the projection (reducer stores it at
      // SET_IDENTITY). Without it the receiver loses the Dragonlord spirit
      // echo + the inspector's original/gained-abilities panels
      // (isDragonIdentityActive requires _originalIdentity).
      if (mc.maskedOriginal !== undefined) {
        card._originalIdentity = mc.maskedOriginal ? { ...mc.maskedOriginal } : null;
      }
      if (borrowStarted) playSound('magic2');
      if (mc.imageUrl) updateCardFrontTexture(mesh, mc.imageUrl);
      host._refreshMovementHUD?.(instanceId);
      aurasDirty = true;
    }

    // Misc flags (underscored on the card instance).
    if (mc.skipNextUntap !== undefined) {
      // Waveshaper's one-shot flag is consumed by PASS_TURN's untapStep, so a
      // true-to-false flip on a card the diff leaves tapped IS the skipped
      // untap. Logged here, once, from the authoritative signal (both seats
      // fold it, so local-only: a broadcast would double the line).
      if (card._skipNextUntap === true && mc.skipNextUntap === false && mc.tapped === true) {
        host.addLogEntry?.(msg('{name} skips untap (Waveshaper)', { name: cardLogLabel(card, msg('A minion')) }), 'system', 'system');

      }
      card._skipNextUntap = mc.skipNextUntap;
    }
    if (mc.skipNextTurn !== undefined) card._skipNextTurn = mc.skipNextTurn;
    if (mc.castLockOwnerId !== undefined) card._castLockOwnerId = mc.castLockOwnerId;
    if (mc.dragonlordCompanion !== undefined) card._dragonlordCompanion = mc.dragonlordCompanion;
    // Both wire names: the server adapter writes `_percevalTargetCell`, the
    // client flags command stores the clean name. Accept either.
    if (mc._percevalTargetCell !== undefined) card._percevalTargetCell = mc._percevalTargetCell;
    else if (mc.percevalTargetCell !== undefined) card._percevalTargetCell = mc.percevalTargetCell;
    if (mc.realmArchive !== undefined) card._realmArchive = mc.realmArchive;
    if (mc._judasCaster !== undefined) card._judasCaster = mc._judasCaster;
    // Wiccan Tools' bearer threshold waiver (server state, swept at the
    // boundary. The sweep DELETES the key, so fold the boolean shape, not a
    // presence check). Affordability (findValidSpellcasters / owner-lock)
    // reads it; the aura recompute refreshes the hand glow.
    if (!!card._spellcasterNoThresholdThisTurn !== !!mc._spellcasterNoThresholdThisTurn) {
      card._spellcasterNoThresholdThisTurn = !!mc._spellcasterNoThresholdThisTurn;
      aurasDirty = true;
    }
    // Site "provides (E) this turn" affinity (Blooms). Server-owned (stamped by the
    // grant_threshold op, cleared at turn end); the synced regions.js reads it for
    // the per-cell affinity + threshold pool, so a change re-runs the affinity calc.
    if (mc._providesThisTurn !== undefined
      && JSON.stringify(mc._providesThisTurn) !== JSON.stringify(card._providesThisTurn ?? null)) {
      card._providesThisTurn = mc._providesThisTurn ? { ...mc._providesThisTurn } : null;
      affinityDirty = true;
    }
    if (mc.abilitiesUsedThisTurn !== undefined
      && changed([...(card._abilitiesUsedThisTurn || [])].sort(), [...mc.abilitiesUsedThisTurn].sort())) {
      card._abilitiesUsedThisTurn = new Set(mc.abilitiesUsedThisTurn);
    }
    if (mc.abilitiesUsedThisGame !== undefined
      && changed([...(card._abilitiesUsedThisGame || [])].sort(), [...mc.abilitiesUsedThisGame].sort())) {
      card._abilitiesUsedThisGame = new Set(mc.abilitiesUsedThisGame);
    }
  }

  if (aurasDirty) host.recomputeStaticAuras?.();
  if (affinityDirty) host.recalculateSiteAffinities?.();
}
